/**
 * Create a service referral from a member's profile (TRUS-757, contract item 9).
 *
 * Replaces the legacy CreateReferralForm on the profile, which wrote the
 * `referrals` table (no receiver, no closed loop). This posts to
 * POST /api/service-referrals, so the referral lands in the Referral Hub queue
 * (/api/tco/service-referrals). The member context (item 10) is shown above the
 * form, read-only, from the org's member field configuration.
 */

import React, { useState } from 'react';
import { CheckCircle2, Loader2, Plus, X } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ServiceTaxonomySelector } from '@/components/service-referrals/ServiceTaxonomySelector';
import { getApiErrorMessage } from '@/utils/apiErrors';
import {
  closedLoopReferralsAPI,
  type ClosedLoopUrgency,
  type CreateServiceReferralRequest,
  type CreatedServiceReferral,
} from '@/services/api/closedLoopReferrals';
import { MemberContextPanel } from './MemberContextPanel';
import type { TFn } from './referralLabels';

const URGENCIES: ClosedLoopUrgency[] = ['routine', 'urgent', 'emergent'];

function urgencyLabel(t: TFn, u: ClosedLoopUrgency): string {
  if (u === 'urgent') return t('referralHub.create.urgent', 'Urgent');
  if (u === 'emergent') return t('referralHub.create.emergent', 'Emergent');
  return t('referralHub.create.routine', 'Routine');
}

export interface ServiceReferralCreateFormProps {
  memberId: number;
  onCancel?: () => void;
  /** Called after the referral is created (the form then shows a confirmation). */
  onCreated?: (referral: CreatedServiceReferral) => void;
  /** Called when the person closes the confirmation. */
  onDone?: () => void;
  /** Open the new referral in the hub. */
  onOpenReferral?: (referralId: number) => void;
  /** TRUS-762: file the referral under this case. */
  caseEpisodeId?: number | null;
}

export function buildCreateRequest(
  memberId: number,
  v: {
    serviceTypeCode: string;
    urgency: ClosedLoopUrgency;
    description: string;
    clinicalNotes: string;
    needs: string[];
    autoMatch: boolean;
    startWorkflow: boolean;
  }
): CreateServiceReferralRequest {
  const body: CreateServiceReferralRequest = {
    member_id: memberId,
    service_type_code: v.serviceTypeCode,
    urgency_level: v.urgency,
    service_description: v.description.trim(),
    auto_match: v.autoMatch,
  };
  if (v.clinicalNotes.trim()) body.clinical_notes = v.clinicalNotes.trim();
  const needs = v.needs.map((n) => n.trim()).filter(Boolean);
  if (needs.length) body.sdoh_needs = needs;
  if (v.startWorkflow) body.start_workflow = true;
  return body;
}

export function ServiceReferralCreateForm({ memberId, onCancel, onCreated, onDone, onOpenReferral, caseEpisodeId }: ServiceReferralCreateFormProps) {
  const { t } = useAppTranslation();
  const [serviceTypeCode, setServiceTypeCode] = useState('');
  const [urgency, setUrgency] = useState<ClosedLoopUrgency>('routine');
  const [description, setDescription] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [needs, setNeeds] = useState<string[]>([]);
  const [needDraft, setNeedDraft] = useState('');
  const [autoMatch, setAutoMatch] = useState(true);
  const [startWorkflow, setStartWorkflow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedServiceReferral | null>(null);

  const addNeed = () => {
    const n = needDraft.trim();
    if (n && !needs.includes(n)) setNeeds([...needs, n]);
    setNeedDraft('');
  };

  const canSubmit = !!serviceTypeCode && !!description.trim() && !submitting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = buildCreateRequest(memberId, { serviceTypeCode, urgency, description, clinicalNotes, needs, autoMatch, startWorkflow });
      if (caseEpisodeId) body.case_episode_id = caseEpisodeId;
      const res = await closedLoopReferralsAPI.createReferral(body);
      setCreated(res);
      onCreated?.(res);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-v2border bg-white p-6 text-center dark:bg-surface" data-testid="service-referral-created">
        <CheckCircle2 className="h-10 w-10 text-success-ink" aria-hidden="true" />
        <p className="font-medium text-ink">
          {t('referralHub.create.created', 'Referral {{number}} created', { number: created.referral_number })}
        </p>
        <p className="text-sm text-ink-500">
          {autoMatch
            ? t('referralHub.create.createdAutoMatch', 'Matching providers are being found. Send it from the Referral Hub.')
            : t('referralHub.create.createdManual', 'Find a provider for it in the Referral Hub.')}
        </p>
        <div className="flex gap-2">
          {onOpenReferral && (
            <Button variant="outline" onClick={() => onOpenReferral(created.id)}>
              {t('referralHub.common.openWorkspace', 'Open workspace')}
            </Button>
          )}
          <Button onClick={() => onDone?.()}>{t('referralHub.common.done', 'Done')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <MemberContextPanel memberId={memberId} />

      <form className="space-y-4 rounded-lg border border-v2border bg-white p-4 dark:bg-surface" onSubmit={submit} data-testid="service-referral-create-form">
        <h3 className="text-base font-semibold text-ink">{t('referralHub.create.title', 'New referral')}</h3>

        <div className="space-y-1.5">
          <Label>{t('referralHub.create.service', 'Service needed')}</Label>
          <ServiceTaxonomySelector value={serviceTypeCode} onChange={(code) => setServiceTypeCode(code)} />
        </div>

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium">{t('referralHub.create.urgency', 'Urgency')}</legend>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {URGENCIES.map((u) => (
              <Button
                key={u}
                type="button"
                size="sm"
                role="radio"
                aria-checked={urgency === u}
                variant={urgency === u ? 'default' : 'outline'}
                onClick={() => setUrgency(u)}
              >
                {urgencyLabel(t, u)}
              </Button>
            ))}
          </div>
        </fieldset>

        <div className="space-y-1.5">
          <Label htmlFor="sr-description">{t('referralHub.create.description', 'What the member needs')}</Label>
          <Textarea
            id="sr-description"
            rows={3}
            maxLength={4000}
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('referralHub.create.descriptionPlaceholder', 'Why you are referring, and what the provider should know.')}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sr-need">{t('referralHub.create.needs', 'Needs')}</Label>
          <div className="flex gap-2">
            <Input
              id="sr-need"
              value={needDraft}
              onChange={(e) => setNeedDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addNeed();
                }
              }}
              placeholder={t('referralHub.create.needsPlaceholder', 'e.g. food, housing, transportation')}
            />
            <Button type="button" variant="outline" onClick={addNeed} disabled={!needDraft.trim()}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{t('referralHub.create.addNeed', 'Add need')}</span>
            </Button>
          </div>
          {needs.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {needs.map((n) => (
                <li key={n} className="inline-flex items-center gap-1 rounded-full bg-paper-2 px-2 py-0.5 text-xs text-ink">
                  {n}
                  <button
                    type="button"
                    onClick={() => setNeeds(needs.filter((x) => x !== n))}
                    aria-label={t('referralHub.create.removeNeed', 'Remove {{need}}', { need: n })}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sr-clinical">{t('referralHub.create.clinicalNotes', 'Clinical notes (optional)')}</Label>
          <Textarea id="sr-clinical" rows={2} maxLength={4000} value={clinicalNotes} onChange={(e) => setClinicalNotes(e.target.value)} />
        </div>

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={autoMatch} onChange={(e) => setAutoMatch(e.target.checked)} />
            <span>
              <span className="font-medium text-ink">{t('referralHub.create.autoMatch', 'Find providers automatically')}</span>
              <span className="block text-xs text-ink-500">
                {t('referralHub.create.autoMatchHint', 'Runs auto-match as soon as the referral is created.')}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={startWorkflow} onChange={(e) => setStartWorkflow(e.target.checked)} />
            <span>
              <span className="font-medium text-ink">{t('referralHub.create.startWorkflow', 'Start the closed-loop workflow')}</span>
              <span className="block text-xs text-ink-500">
                {t('referralHub.create.startWorkflowHint', 'Tracks the referral from sending through to the outcome, with reminders.')}
              </span>
            </span>
          </label>
        </div>

        {error && (
          <p className="text-sm text-danger-ink" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              {t('referralHub.common.cancel', 'Cancel')}
            </Button>
          )}
          <Button type="submit" disabled={!canSubmit}>
            {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />}
            {t('referralHub.create.submit', 'Create referral')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ServiceReferralCreateForm;
